import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  Stack,
  Text,
  Badge,
  Group,
  SimpleGrid,
  Progress,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconDatabase,
  IconBrandReddit,
  IconCloud,
  IconBrain,
  IconCircleCheck,
  IconAlertTriangle,
  IconCircleX,
} from '@tabler/icons-react';
import { getHealthStatus } from '../../services/systemService';
import type { HealthCheckResponse, ServiceHealthStatus } from '../../types/api';

const ServiceIcon = ({ service }: { service: string }) => {
  const iconProps = { size: 20 };
  switch (service) {
    case 'database':
      return <IconDatabase {...iconProps} />;
    case 'redis':
      return <IconBrandReddit {...iconProps} />; // Using Reddit icon as Redis icon not available
    case 'storage':
      return <IconCloud {...iconProps} />;
    case 'ai_engine':
      return <IconBrain {...iconProps} />;
    default:
      return <IconCircleCheck {...iconProps} />;
  }
};

const StatusIcon = ({ status }: { status: 'up' | 'down' }) => {
  if (status === 'up') {
    return <IconCircleCheck size={16} />;
  }
  return <IconCircleX size={16} />;
};

const getStatusColor = (status: 'healthy' | 'degraded' | 'unhealthy') => {
  switch (status) {
    case 'healthy':
      return 'green';
    case 'degraded':
      return 'yellow';
    case 'unhealthy':
      return 'red';
    default:
      return 'gray';
  }
};

const ServiceCard = ({
  name,
  service,
}: {
  name: string;
  service: ServiceHealthStatus;
}) => {
  const displayName = name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  const color = service.status === 'up' ? 'green' : 'red';

  return (
    <Card withBorder shadow="sm">
      <Stack gap="xs">
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon color={color} size="lg" radius="md">
              <ServiceIcon service={name} />
            </ThemeIcon>
            <Text fw={500}>{displayName}</Text>
          </Group>
          <Badge
            color={color}
            leftSection={<StatusIcon status={service.status} />}
          >
            {service.status.toUpperCase()}
          </Badge>
        </Group>
        <Progress
          value={service.status === 'up' ? 100 : 0}
          color={color}
          size="sm"
        />
        <Group justify="space-between">
          <Text size="xs" c="dimmed">Response Time</Text>
          <Text size="xs" fw={500}>{service.response_time}ms</Text>
        </Group>
        {service.message && (
          <Text size="xs" c="dimmed">{service.message}</Text>
        )}
      </Stack>
    </Card>
  );
};

export const SystemHealthStatus: React.FC = () => {
  const { data, isLoading, error } = useQuery<HealthCheckResponse>({
    queryKey: ['system', 'health'],
    queryFn: getHealthStatus,
    refetchInterval: 30000, // Refetch every 30 seconds
    retry: false, // Don't retry on failure
  });

  if (isLoading) {
    return (
      <Card withBorder shadow="sm">
        <Stack>
          <Title order={4}>System Health</Title>
          <Text size="sm" c="dimmed">Checking system health...</Text>
        </Stack>
      </Card>
    );
  }

  if (error) {
    return (
      <Card withBorder shadow="sm">
        <Stack>
          <Title order={4}>System Health</Title>
          <Badge color="red">Error checking health</Badge>
          <Text size="sm" c="dimmed">
            Unable to retrieve system health status
          </Text>
        </Stack>
      </Card>
    );
  }

  if (!data) return null;

  const overallIcon = data.status === 'healthy' ? (
    <IconCircleCheck size={20} />
  ) : data.status === 'degraded' ? (
    <IconAlertTriangle size={20} />
  ) : (
    <IconCircleX size={20} />
  );

  return (
    <Stack gap="md">
      <Card withBorder shadow="sm">
        <Stack gap="sm">
          <Group justify="space-between">
            <Title order={4}>System Health</Title>
            <Badge
              size="lg"
              color={getStatusColor(data.status)}
              leftSection={overallIcon}
            >
              {data.status.toUpperCase()}
            </Badge>
          </Group>
          <Text size="sm" c="dimmed">
            Last checked: {new Date(data.timestamp).toLocaleTimeString()}
          </Text>
          {data.message && (
            <Text size="sm" c="red">{data.message}</Text>
          )}
        </Stack>
      </Card>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        {Object.entries(data.services).map(([name, service]) => (
          <ServiceCard key={name} name={name} service={service} />
        ))}
      </SimpleGrid>
    </Stack>
  );
};